import { GetServerSidePropsContext, GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from "next";
import { drupal } from "lib/utils/drupal/drupal";
import { Node, NodeProps } from "components/node";
import { homeTitle, revalidateTimeout } from "lib/constants";
import { DrupalJsonApiParams } from "drupal-jsonapi-params";
import { getGlobalElements } from "lib/utils/drupal/get-global-elements";
import Layout from '@/components/layouts/Layout';
import { LayoutProps } from "@/components/layouts/BaseLayout";
import { getResourcePath } from '@/utils/drupal/get-resource-path';
import getContent from "@/utils/drupal/get-content";

const RESOURCE_TYPES = [
    "node--landing_page",
    "node--legal_page",
    "node--article",
    "node--recipe",
    "shopify_product--shopify_product",
    "taxonomy_term--article_category"
];

const BUILD_RESOURCE_TYPES = [
    "node--landing_page",
    "node--legal_page",
    "shopify_product--shopify_product",
];

const SLUG_PAGES = [
    "",
    "es/trazabilidad-jamon",
    "es/quienes-somos",
    "es/fabrica-navidul",
    "es/cesta",
    "es/escuela-del-jamon",
    "es/escuela-del-jamon/recetas",
];

export interface NodePageProps extends NodeProps, LayoutProps {
    theme: boolean,
    preFooter: boolean,
}

export default function NodePage({
    content,
    blocks,
    theme,
    preFooter,
    meta,
    canonical,
    ...props
}: NodePageProps) {

    return (
        <Layout blocks={blocks} theme={theme ? 'dark' : 'light'} layoutType='base' preFooter={preFooter} meta={meta} canonical={canonical}>
            <div>
                <Node content={content} {...props} />
            </div>
        </Layout>
    )
}

export async function getStaticPaths(context): Promise<GetStaticPathsResult> {
    const params = new DrupalJsonApiParams();
    if (!context.preview) {
        params.addFilter("status", "1");
    }
    const p = await drupal.getStaticPathsFromContext(BUILD_RESOURCE_TYPES, context, { params: params.getQueryObject() }).then((paths) =>
        paths.filter((item) => {
            const slug = item["params"]["slug"].join("/");
            return !SLUG_PAGES.includes(slug);

        }));

    params.addSort("created", "DESC");
    params.addPageLimit(20);
    const articlePaths = await drupal.getStaticPathsFromContext(["node--article"], context, { params: params.getQueryObject() }).then((paths) =>
        paths.filter((item) => {
            const slug = item["params"]["slug"].join("/");
            return !SLUG_PAGES.includes(slug);

        }));

    const recipePaths = await drupal.getStaticPathsFromContext(["node--recipe"], context, { params: params.getQueryObject() }).then((paths) =>
        paths.filter((item) => {
            const slug = item["params"]["slug"].join("/");
            return !SLUG_PAGES.includes(slug);

        }));

    return {
        paths: p.concat(articlePaths).concat(recipePaths),
        fallback: "blocking",
    };

}


export async function getStaticProps(
    context: GetStaticPropsContext | GetServerSidePropsContext
): Promise<GetStaticPropsResult<NodePageProps>> {
    const path = await drupal.translatePathFromContext(context);
    const resource = await getResourcePath(path, context);
    if (resource == null) {
        return {
            notFound: true,
            revalidate: revalidateTimeout,
        };
    }
    const type = path.jsonapi?.resourceName;
    if (!RESOURCE_TYPES.includes(type)) {
        return {
            notFound: true,
            revalidate: revalidateTimeout,
        };
    }
    if (path.label === homeTitle) {
        return {
            redirect: {
                permanent: false,
                destination: "/"
            }
        }
    }
    const slug = encodeURI(Array.isArray(context.params.slug) ? `/${context.params.slug.join("/")}` : `/${context.params.slug}`);
    if (path.entity.path && !context.preview && slug != path.entity.path) {
        return {
            redirect: {
                permanent: false,
                destination: path.entity.path,
            },
        };
    }

    const contentObject = await getContent(resource, type, context);


    return {
        props: {
            ...(await getGlobalElements(context)),
            content: contentObject.content,
            theme: contentObject.content.node.field_dark_banner != undefined ? contentObject.content.node.field_dark_banner : false,
            preFooter: contentObject.content.node.field_show_prefooter != undefined ? contentObject.content.node.field_show_prefooter : true,
            meta: contentObject.content.node.metatag ? contentObject.content.node.metatag : null,
            canonical: path.entity.path ? path.entity.path : slug
        },
        revalidate: revalidateTimeout
    };
}
